import { useCallback } from 'react';
import { BatchExportParameters } from 'types/exportObjects.type';
import { CommonBatchExportParameters } from 'types/exportObjects.type';
import { errorNotification, successNotification } from 'utils/notifications';
import { FormInstance } from '@timeedit/ui-components/antd';
import {
  ReuseJobParams,
  StartJobParams,
  useReuseJobMutation,
  useStartJobMutation,
} from '../components/JobResults/api/objectJobApi';
import {
  useReuseExportJobMutation,
  useStartExportJobMutation,
} from '../components/export/api/exportObjectsApi';

type BaseSubmitParams = {
  selection: JobObjectSelection;
  form: FormInstance<any>;
  setSendingBatch: (sending: boolean) => void;
  setOpen: (openSection: false) => void;
  successMessage: string;
  onSuccess?: (id: string) => void;
};

export type BulkEditSubmitParams = BaseSubmitParams & {
  startJob: ReturnType<typeof useStartJobMutation>[0];
  reuseJob: ReturnType<typeof useReuseJobMutation>[0];
  getFetchArgs: () => Promise<
    Omit<StartJobParams, 'filter'> | Omit<ReuseJobParams, 'jobId'>
  >;
};

export type ExportSubmitParams = BaseSubmitParams & {
  startJob: ReturnType<typeof useStartExportJobMutation>[0];
  reuseJob: ReturnType<typeof useReuseExportJobMutation>[0];
  getFetchArgs: () => Promise<CommonBatchExportParameters>;
};

export type JobObjectSelection =
  | { filter: Record<string, unknown> }
  | { jobId: string };

export const useJobSubmit = <
  T extends BulkEditSubmitParams | ExportSubmitParams,
>({
  form,
  setSendingBatch,
  setOpen,
  selection,
  successMessage,
  onSuccess,
  startJob,
  reuseJob,
  getFetchArgs,
}: T) => {
  return useCallback(async () => {
    try {
      setSendingBatch(true);
      const fetchArgs = await getFetchArgs();

      const work = () => {
        if ('filter' in selection) {
          return startJob({
            ...fetchArgs,
            filter: selection.filter,
          } as any).unwrap();
        } else if (reuseJob) {
          return reuseJob({
            ...fetchArgs,
            jobId: selection.jobId,
          } as any).unwrap();
        }
      };

      const result = await work();

      successNotification(successMessage);
      onSuccess?.(result.id);
      setOpen(false);
    } catch (error: any) {
      // Handle AntD form validation errors separately: let inline validation display.
      if (error?.errorFields) {
        return;
      }
      const errorTitle = error?.data?.message ?? 'Batch operation failed';
      const errorDetails: string[] | undefined = Array.isArray(
        error?.data?.errors
      )
        ? error.data.errors.map(
            (error: { message: string; fieldId: number }) =>
              `Field ${error.fieldId}: ${error.message}`
          )
        : error?.message != null
          ? [error.message]
          : undefined;
      errorNotification(errorTitle, errorDetails);
    } finally {
      setSendingBatch(false);
    }
  }, [
    selection,
    form,
    setOpen,
    setSendingBatch,
    successMessage,
    onSuccess,
    startJob,
    reuseJob,
    getFetchArgs,
  ]);
};
